/**
 * User Lifecycle Helper for Analytics
 * Calculates user engagement metrics and lifecycle stages for PostHog tracking
 */

import logger from './logger';

/**
 * Calculate user lifecycle stage based on behavior patterns
 * @param {Object} userData - User engagement data
 * @returns {string} Lifecycle stage
 */
export const calculateUserLifecycleStage = (userData) => {
  if (!userData) return 'unknown';

  const daysSinceSignup = userData.days_since_signup || 0;
  const daysSinceLastActive = userData.last_active_days_ago || 0;
  const totalSessions = userData.total_sessions || 0;
  const reportsGenerated = userData.total_reports_generated || 0;
  const productsViewed = userData.total_products_viewed || 0;

  // Dormant: No activity in 30+ days
  if (daysSinceLastActive > 30) {
    return 'dormant';
  }

  // At Risk: No activity in 14+ days but was active before
  if (daysSinceLastActive > 14 && totalSessions > 3) {
    return 'at_risk';
  }

  // Power User: 5+ reports OR 20+ sessions OR 50+ products viewed
  if (reportsGenerated >= 5 || totalSessions >= 20 || productsViewed >= 50) {
    return 'power_user';
  }

  // Active: 3+ sessions and at least 1 report
  if (totalSessions >= 3 && reportsGenerated >= 1) {
    return 'active';
  }

  // New: First 7 days
  if (daysSinceSignup <= 7) {
    return 'new';
  }

  // Engaged: Some activity but not yet active
  if (totalSessions >= 2 || productsViewed >= 5) {
    return 'engaged';
  }

  return 'new';
};

/**
 * Get comprehensive user lifecycle properties for identification
 * This queries the database for full engagement stats
 * @param {string} userId - User ID
 * @param {Object} supabase - Supabase client instance
 * @returns {Object} User lifecycle properties
 */
export const getUserLifecycleProperties = async (userId, supabase) => {
  try {
    // Call the RPC function to get engagement stats
    const { data: stats, error } = await supabase.rpc('get_user_engagement_stats', {
      p_user_id: userId,
    });

    if (error) {
      logger.error('[UserLifecycle] Error fetching engagement stats:', error);
      return getDefaultLifecycleProperties();
    }

    if (!stats || stats.length === 0) {
      return getDefaultLifecycleProperties();
    }

    const userStats = stats[0]; // RPC returns array

    // Fetch redesign feed stats (non-blocking — defaults to 0 if unavailable)
    let feedProps = {};
    try {
      feedProps = await getRedesignFeedProperties(userId, supabase);
    } catch (feedErr) {
      logger.error('[UserLifecycle] Error fetching feed properties:', feedErr);
    }

    return {
      days_since_signup: userStats.days_since_signup || 0,
      user_lifecycle_stage: calculateUserLifecycleStage(userStats),
      total_products_viewed: userStats.products_viewed_count || 0,
      total_reports_generated: userStats.reports_generated_count || 0,
      total_reports_downloaded: userStats.reports_downloaded_count || 0,
      total_sessions: userStats.session_count || 0,
      last_active_days_ago: userStats.days_since_last_active || 0,
      has_completed_onboarding: userStats.onboarding_completed || false,
      has_generated_report: (userStats.reports_generated_count || 0) > 0,
      has_downloaded_report: (userStats.reports_downloaded_count || 0) > 0,
      has_shared_recommendation: (userStats.snapshots_created_count || 0) > 0,
      has_expressed_feature_interest: (userStats.feature_interests_count || 0) > 0,
      engagement_score: calculateEngagementScore(userStats),
      ...feedProps,
    };
  } catch (error) {
    logger.error('[UserLifecycle] Exception fetching lifecycle properties:', error);
    return getDefaultLifecycleProperties();
  }
};

/**
 * Get basic lifecycle properties without database call
 * Useful for events where we want minimal latency
 * @param {Object} user - User object from auth
 * @param {Object} cachedStats - Optional cached stats
 * @returns {Object} Basic lifecycle properties
 */
export const getBasicLifecycleProperties = (user, cachedStats = null) => {
  if (!user) return {};

  const daysSinceSignup = calculateDaysSince(user.created_at);

  return {
    days_since_signup: daysSinceSignup,
    user_lifecycle_stage: cachedStats
      ? calculateUserLifecycleStage(cachedStats)
      : daysSinceSignup <= 7
        ? 'new'
        : 'engaged',
    user_created_at: user.created_at,
    user_email_verified: !!user.email_confirmed_at,
  };
};

/**
 * Calculate an engagement score (0-100) based on user activity
 * @param {Object} userData - User engagement data
 * @returns {number} Engagement score
 */
export const calculateEngagementScore = (userData) => {
  if (!userData) return 0;

  let score = 0;

  // Reports generated (30 points max, 10 points per report)
  score += Math.min((userData.reports_generated_count || 0) * 10, 30);

  // Products viewed (20 points max, 1 point per view)
  score += Math.min(userData.products_viewed_count || 0, 20);

  // Reports downloaded (15 points max, 5 points per download)
  score += Math.min((userData.reports_downloaded_count || 0) * 5, 15);

  // Onboarding completed (10 points)
  if (userData.onboarding_completed) score += 10;

  // Feature interest expressed (5 points)
  if ((userData.feature_interests_count || 0) > 0) score += 5;

  // Shared recommendation (10 points)
  if ((userData.snapshots_created_count || 0) > 0) score += 10;

  // Recency bonus (10 points if active in last 7 days)
  if ((userData.days_since_last_active || 999) <= 7) score += 10;

  return Math.min(score, 100);
};

/**
 * Calculate days since a given date
 * @param {string|Date} dateString - Date to calculate from
 * @returns {number} Days since date
 */
export const calculateDaysSince = (dateString) => {
  if (!dateString) return 0;

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  } catch (error) {
    logger.error('[UserLifecycle] Error calculating days since:', error);
    return 0;
  }
};

/**
 * Get redesign feed properties for PostHog user identification.
 * Queries user_listing_feeds and user_listing_feed_entries for feed-related metrics.
 *
 * @param {string} userId - User ID
 * @param {Object} supabase - Supabase client instance
 * @returns {Object} Feed properties
 */
const getRedesignFeedProperties = async (userId, supabase) => {
  // Check active feeds
  const { count: activeFeedCount } = await supabase
    .from('user_listing_feeds')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('status', 'active');

  // Count selected models
  const { count: selectedModelCount } = await supabase
    .from('user_model_fit_scores')
    .select('user_model_fit_score_id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('selected_for_feed', true);

  // Count total feed listings
  const { count: totalFeedListings } = await supabase
    .from('user_listing_feeds')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .is('dismissed_at', null);

  // Count saved listings
  const { count: totalSavedListings } = await supabase
    .from('user_listing_feeds')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .not('saved_at', 'is', null);

  // Count dismissed listings
  const { count: totalDismissedListings } = await supabase
    .from('user_listing_feeds')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .not('dismissed_at', 'is', null);

  return {
    has_active_feed: (activeFeedCount || 0) > 0,
    selected_model_count: selectedModelCount || 0,
    total_feed_listings: totalFeedListings || 0,
    total_saved_listings: totalSavedListings || 0,
    total_dismissed_listings: totalDismissedListings || 0,
  };
};

/**
 * Get default lifecycle properties for error cases
 * @returns {Object} Default properties
 */
const getDefaultLifecycleProperties = () => ({
  days_since_signup: 0,
  user_lifecycle_stage: 'unknown',
  total_products_viewed: 0,
  total_reports_generated: 0,
  total_reports_downloaded: 0,
  total_sessions: 0,
  last_active_days_ago: 0,
  has_completed_onboarding: false,
  has_generated_report: false,
  has_downloaded_report: false,
  has_shared_recommendation: false,
  has_expressed_feature_interest: false,
  engagement_score: 0,
});

/**
 * Check if user should be considered "high intent"
 * @param {Object} userData - User engagement data
 * @returns {boolean} True if high intent user
 */
export const isHighIntentUser = (userData) => {
  if (!userData) return false;

  const engagementScore = calculateEngagementScore(userData);
  const lifecycleStage = calculateUserLifecycleStage(userData);

  return (
    engagementScore >= 50 ||
    lifecycleStage === 'power_user' ||
    lifecycleStage === 'active' ||
    (userData.reports_generated_count || 0) >= 2
  );
};

/**
 * Get user segment for targeting
 * @param {Object} userData - User engagement data
 * @returns {string} User segment
 */
export const getUserSegment = (userData) => {
  if (!userData) return 'unknown';

  const lifecycleStage = calculateUserLifecycleStage(userData);
  const reportsGenerated = userData.reports_generated_count || 0;

  if (lifecycleStage === 'power_user') return 'power_user';
  if (lifecycleStage === 'at_risk' || lifecycleStage === 'dormant') return 'churn_risk';
  if (reportsGenerated === 0 && (userData.days_since_signup || 0) > 7) return 'never_converted';
  if (reportsGenerated > 0 && reportsGenerated < 3) return 'occasional_user';
  if (lifecycleStage === 'new') return 'new_user';

  return 'active_user';
};
